'use client';

import type { ReactElement, CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import type { PaddingBox } from '../_shared/resolvePadding';
import { SectionShell } from '../_shared/SectionShell';
import { tx } from '../_shared/coerce';
import { PortalLink, useBlockPart } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { cardReset } from '../_shared/styles';

/**
 * Default fallback renderer for the {@code PillarStack} block.
 *
 * <p>Rich theme-specific versions ship in individual theme bundles
 * (e.g. {@code @akademi/theme-btk}). When the active theme registers
 * its own {@code PillarStack}, the theme bundle's override takes
 * precedence via the block resolver. This file is the canonical
 * fallback every other theme falls back to so a tenant that authors a
 * {@code PillarStack} block doesn't see "Unknown block type" when a
 * non-BTK theme is active.
 *
 * <p>Visual: a 3-column responsive grid of vertical cards. No "pillar"
 * wave effect, no hover lifts — that's BTK's signature look, not the
 * canonical one. Themes that want that effect ship their own override.
 *
 * <p>Authoring fields:
 * <ul>
 *   <li>{@code title} — section heading (defaults to the catalogue wording)</li>
 *   <li>{@code baseHeight} — card height in px (200-600, default 380)</li>
 *   <li>{@code cards} — array of
 *       {@code { title, body, href, image, imageAlt, featured }}</li>
 * </ul>
 */
export interface PillarStackCard {
  title: string;
  body?: string;
  href?: string;
  image?: string;
  imageAlt?: string;
  featured?: boolean;
}

/** Authored strings arrive either plain or as a per-locale bundle. */
type Localized = string | Record<string, string>;

export interface PillarStackProps {
  title?: Localized;
  /** Supporting line under the section heading. */
  subtitle?: Localized;
  cards?: PillarStackCard[];
  baseHeight?: number;
  style?: CSSProperties;
  paddingOuter?: PaddingBox;
  /** Draw the decorative gradient rule beside the section heading. */
  headingLine?: boolean;
  paddingInner?: PaddingBox;
}

export function PillarStack({
  title,
  subtitle,
  cards = [],
  baseHeight = 380,
  style,
  paddingOuter,
  // matches the BTK override + this block's schema default — the rule is
  // part of the PillarStack look, not an opt-in extra
  headingLine = true,
  paddingInner,
}: PillarStackProps): ReactElement {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  return (
    <SectionShell
      headingLine={headingLine}
      title={tx(title) || t('grid.pillarStack.title')}
      subtitle={tx(subtitle)}
      style={style}
      paddingOuter={paddingOuter}
      paddingInner={paddingInner}
    >
      {cards.length === 0 ? (
        <EmptyHint />
      ) : (
        <div
          {...part('list')}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 'var(--space-5, 20px)',
          }}
        >
          {cards.map((card, i) => (
            <PillarTile key={i} card={card} baseHeight={baseHeight} index={i} />
          ))}
        </div>
      )}
    </SectionShell>
  );
}

function PillarTile({
  card,
  baseHeight,
  index,
}: {
  card: PillarStackCard;
  baseHeight: number;
  index: number;
}) {
  const part = useBlockPart();
  const inner = (
    <Card
      {...(card.href ? {} : part('item', index))}
      style={{
        ...cardReset,
        position: 'relative',
        height: baseHeight,
        borderRadius: 'var(--radius-lg, 12px)',
        overflow: 'hidden',
        background: card.image
          ? `linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,0.7) 100%), url(${card.image}) center/cover no-repeat`
          : 'var(--color-neutral-100, #f4f4f5)',
        color: card.image ? '#fff' : 'var(--color-text-primary, #18181b)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        padding: 'var(--space-5, 20px)',
      }}
    >
      {card.image && card.imageAlt && (
        <span style={{ position: 'absolute', left: -9999 }}>{card.imageAlt}</span>
      )}
      <h3
        style={{
          margin: '0 0 8px',
          fontSize: 'var(--font-size-lg, 18px)',
          fontWeight: 700,
          lineHeight: 1.25,
        }}
      >
        {card.title}
      </h3>
      {card.body && (
        <p
          style={{
            margin: 0,
            fontSize: 'var(--font-size-sm, 14px)',
            lineHeight: 1.5,
            color: card.image ? 'rgba(255,255,255,0.85)' : 'var(--color-text-secondary, #52525b)',
          }}
        >
          {card.body}
        </p>
      )}
    </Card>
  );

  if (card.href) {
    return (
      <PortalLink
        href={card.href}
        {...part('item', index)}
        style={{ textDecoration: 'none', color: 'inherit' }}
      >
        {inner}
      </PortalLink>
    );
  }
  return inner;
}

function EmptyHint() {
  const { t } = useTranslation('site-builder-blocks');
  return (
    <div
      style={{
        padding: 'var(--space-6, 24px)',
        textAlign: 'center',
        border: '1px dashed var(--color-neutral-300, #d4d4d8)',
        borderRadius: 'var(--radius-md, 8px)',
        color: 'var(--color-text-secondary, #71717a)',
        fontSize: 'var(--font-size-sm, 14px)',
      }}
    >
      {t('grid.pillarStack.empty')}
    </div>
  );
}
